import { Button, Modal, Select } from '@buildbox/components'
import cogoToast from 'cogo-toast'
import React, { ChangeEvent, useCallback, useEffect, useState } from 'react'
import ImageUpload from 'shared/components/ImageUpload'
import { IImageUpload } from 'shared/components/ImageUpload/types'
import { uploadToS3 } from 'shared/services/aws.service'
import { saveNewBooklet, updateBooklet } from 'shared/services/booklet.service'
import {
	primaryDashboardButtonStyles,
	primaryInputTicketStyles,
} from 'shared/styles/theme'
import cogoDefaultOptions from 'shared/util/toaster'

import { isAfter, isFuture, isSameDay, parseISO } from 'date-fns'
import DateInpput from 'shared/components/DateInput'
import InputPrimary from 'shared/components/InputPrimary'
import { Container } from './styles'
import { IProps } from './types'
import { getImageSize, IGetImageSizeProps } from 'shared/util/image'

function ModalBooklet(props: IProps) {
	const {
		isActive,
		closeModal,
		selectedRetail,
		selectedBooklet,
		retailOptions,
		handleChangeRetail,
	} = props

	const [coverImage, setCoverImage] = useState<IImageUpload>({
		file: null,
		url: '',
	})
	const [frontImage, setFrontImage] = useState<IImageUpload>({
		file: null,
		url: '',
	})
	const [backImage, setBackImage] = useState<IImageUpload>({
		file: null,
		url: '',
	})

	const [isSubmiting, setIsSubmiting] = useState(false)
	const [isFormValid, setIsFormValid] = useState(false)

	const [title, setTitle] = useState('')
	const [vigor, setVigor] = useState('')
	const [validity, setValidity] = useState('')

	const userRetailOptions = retailOptions.map((userRetail) => ({
		value: userRetail.value,
		label: userRetail.label,
	}))

	const allValueRetails = userRetailOptions.map((retails) => {
		return retails.value
	})

	const userRetailOptionsWithAllRetails = [
		{ value: `${allValueRetails}`, label: 'Todas as Filiais' },
		...userRetailOptions,
	]

	function loadBannerState() {
		if (!isActive) return

		if (selectedBooklet) {
			setTitle(selectedBooklet.title)
			setVigor(selectedBooklet.vigor)
			setValidity(selectedBooklet.validity)

			setCoverImage({ file: null, url: selectedBooklet.coverBookletImage })
			setFrontImage({ file: null, url: selectedBooklet.frontBookletImage })
			setBackImage({ file: null, url: selectedBooklet.backBookletImage })
		}
	}

	function validateDates(vigor: string, validity: string): boolean {
		const vigorDate = parseISO(vigor)
		const validityDate = parseISO(validity)

		const today = new Date()

		return (
			(isFuture(validityDate) || isSameDay(validityDate, today)) &&
			(isAfter(validityDate, vigorDate) || isSameDay(validityDate, vigorDate))
		)
	}

	function validateForm() {
		const isValid =
			!!selectedRetail &&
			(!!coverImage.file || !!coverImage.url) &&
			(!!frontImage.file || !!frontImage.url) &&
			(!!backImage.file || !!backImage.url) &&
			!!title.length &&
			validateDates(vigor, validity)

		setIsFormValid(isValid)
	}

	function clearFiels() {
		setCoverImage({ file: null, url: '' })
		setFrontImage({ file: null, url: '' })
		setBackImage({ file: null, url: '' })
		setTitle('')
		setVigor('')
		setValidity('')
	}

	const onClose = useCallback(
		(refetchData: boolean = false) => {
			clearFiels()
			closeModal({ refetchData, dataToFetch: ['booklet'] })
		},
		[closeModal],
	)

	async function checkAspectRatio(file: File) {
		const dimension = await getImageSize(file)
		const ratio = dimension.width / dimension.height
		const expectedRatio = 16 / 25

		const isValid = Math.abs(ratio - expectedRatio) < 0.01

		return isValid
	}

	const handleChangeImage = useCallback(
		(
			value: IImageUpload,
			inputId: 'cover-image' | 'front-image' | 'back-image',
		) => {
			;(async () => {
				if (!value.file) return

				const isAspectRatioAccepted = await checkAspectRatio(value.file)

				if (!isAspectRatioAccepted) {
					cogoToast.error(
						'O Encarte não está na dimensão correta!',
						cogoDefaultOptions,
					)

					return
				}

				switch (inputId) {
					case 'cover-image':
						setCoverImage(value)
						break
					case 'front-image':
						setFrontImage(value)
						break
					case 'back-image':
						setBackImage(value)
						break
				}
			})()
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	)

	function handleChange(event: ChangeEvent<HTMLInputElement>) {
		const str = event.target.value

		setTitle(str)
	}

	function handleVigorChange(event: ChangeEvent<HTMLInputElement>) {
		const str = event.target.value

		setVigor(str)
	}

	function handleValidityChange(event: ChangeEvent<HTMLInputElement>) {
		const str = event.target.value

		setValidity(str)
	}

	const handleSubmit = useCallback(() => {
		;(async () => {
			if (!isFormValid) return

			try {
				setIsSubmiting(true)

				let coverImageUrl, frontImageUrl, backImageUrl

				if (coverImage.file) {
					const coverImageUrlResponse = await uploadToS3(coverImage.file)
					coverImageUrl = coverImageUrlResponse
				}

				if (frontImage.file) {
					const frontImageUrlResponse = await uploadToS3(frontImage.file)
					frontImageUrl = frontImageUrlResponse
				}

				if (backImage.file) {
					const backImageUrlResponse = await uploadToS3(backImage.file)
					backImageUrl = backImageUrlResponse
				}

				const listRetailsSelected = selectedRetail?.value
					? selectedRetail.value.split(',')
					: [selectedRetail?.value]

				const bookletDetails: any = {
					...selectedBooklet,
					retail: listRetailsSelected,
					title: title,
					vigor: vigor,
					validity: validity,
				}

				if (coverImageUrl) bookletDetails.coverBookletImage = coverImageUrl
				if (frontImageUrl) bookletDetails.frontBookletImage = frontImageUrl
				if (backImageUrl) bookletDetails.backBookletImage = backImageUrl

				if (!!selectedBooklet) {
					await updateBooklet(bookletDetails, selectedBooklet._id)
				} else {
					await saveNewBooklet(bookletDetails)
					cogoToast.success('Encarte salvo com sucesso!', cogoDefaultOptions)
				}
			} finally {
				selectedRetail?.label === 'Todas as Filiais' &&
					handleChangeRetail(retailOptions[0])

				setIsSubmiting(false)
				onClose(true)
			}
		})()
	}, [
		isFormValid,
		coverImage.file,
		frontImage.file,
		backImage.file,
		selectedRetail,
		selectedBooklet,
		title,
		vigor,
		validity,
		handleChangeRetail,
		retailOptions,
		onClose,
	])

	useEffect(validateForm, [
		frontImage,
		backImage,
		selectedRetail,
		title,
		validity,
		vigor,
	])
	useEffect(loadBannerState, [selectedBooklet])

	return (
		<Modal
			isActive={isActive}
			title='Encarte'
			onClose={() => onClose()}
			className='modal-booklet'
			size={624}
		>
			<Container>
				<div className='input-container select-retail'>
					<Select
						round
						placeholder='Loja'
						{...primaryInputTicketStyles}
						id='select-retail'
						options={userRetailOptionsWithAllRetails}
						value={selectedRetail}
						onChange={handleChangeRetail}
					/>
				</div>
				<div className='inputs-group'>
					<div className='input-container' style={{ width: 343 }}>
						<ImageUpload
							returnImage={(value) => handleChangeImage(value, 'cover-image')}
							imageUrl={coverImage.url}
							width='312'
							height='135'
							id='cover-image'
							secondLabel='TAMANHO 737x1161px'
							dimensionLabel='16:25'
						/>
						<span>CAPA</span>
					</div>
					<div className='input-container-wrapper'>
						<div className='input-container'>
							<ImageUpload
								returnImage={(value) => handleChangeImage(value, 'front-image')}
								imageUrl={frontImage.url}
								width='198'
								height='211'
								id='front-image'
								dimensionLabel='16:25'
							/>
							<span>FRENTE</span>
						</div>
						<div className='input-container'>
							<ImageUpload
								returnImage={(value) => handleChangeImage(value, 'back-image')}
								imageUrl={backImage.url}
								width='198'
								height='211'
								id='back-image'
								dimensionLabel='16:25'
							/>
							<span>VERSO</span>
						</div>
					</div>
				</div>

				<div className='form-wrapper'>
					<InputPrimary
						value={title}
						onChange={handleChange}
						label='Título do Encarte'
						maxLength={60}
					/>

					<DateInpput
						value={vigor}
						label='Vigência'
						aria-label='Date'
						type='date'
						onChange={handleVigorChange}
					/>

					<DateInpput
						value={validity}
						label='Validade'
						aria-label='Date'
						type='date'
						onChange={handleValidityChange}
					/>
				</div>

				<Button
					{...primaryDashboardButtonStyles}
					className='submit-button'
					onClick={handleSubmit}
					disabled={isSubmiting || !isFormValid}
				>
					Cadastrar
				</Button>
			</Container>
		</Modal>
	)
}

export default ModalBooklet
